import { normalizeAuthoredBeat } from '@/_pure/episodeAuthoring';
import type { CoreConceptArrangement, CoreConceptBeat, CoreEpisode, CoreScene } from '@/adapters/core/_contract/episode';

function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid Core episode object.');
    return value as Record<string, unknown>;
}
function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Core episode requires nonempty text.');
    return value;
}
function fields(value: Record<string, unknown>, allowed: readonly string[]) {
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key)) throw new Error(`Unknown Core episode field '${key}'.`);
    }
}

function parseCoreConceptArrangement(value: unknown): CoreConceptArrangement {
    const arr = record(value);
    fields(arr, ['arrangementKind']);
    if (arr.arrangementKind !== 'core-overview') throw new Error('Invalid Core concept arrangement kind.');
    return { arrangementKind: 'core-overview' };
}

export function parseCoreEpisode(value: unknown): CoreEpisode {
    const episode = record(value);
    fields(episode, ['handle', 'schemaVersion', 'title', 'scenes']);
    if (episode.schemaVersion !== 2) throw new Error('Unsupported Core episode schema version.');
    const handle = text(episode.handle);
    const title = text(episode.title);
    if (!Array.isArray(episode.scenes) || !episode.scenes.length) throw new Error('Core episode requires scenes.');
    const sceneIds = new Set<string>();
    const scenes: CoreScene[] = episode.scenes.map((value) => {
        const scene = record(value);
        if (scene.sceneKind !== 'concept') throw new Error('Core episodes only support concept scenes.');
        fields(scene, ['sceneKind', 'id', 'title', 'beats']);
        const id = text(scene.id);
        if (sceneIds.has(id)) throw new Error(`Duplicate scene id '${id}'.`);
        sceneIds.add(id);
        const sceneTitle = text(scene.title);
        if (!Array.isArray(scene.beats) || !scene.beats.length) throw new Error('Core concept scene requires beats.');
        const beatIds = new Set<string>();
        const beats: CoreConceptBeat[] = scene.beats.map((value) => {
            const beat = normalizeAuthoredBeat(value, 'concept');
            fields(beat, ['id', 'arrangement', 'explanation', 'spokenText', 'narrationTextMap', 'narrationPauses', 'readingHoldMs']);
            const beatId = text(beat.id);
            if (beatIds.has(beatId)) throw new Error(`Duplicate concept beat '${beatId}'.`);
            beatIds.add(beatId);
            if (typeof beat.readingHoldMs !== 'number' || !Number.isFinite(beat.readingHoldMs) || beat.readingHoldMs < 0) {
                throw new Error(`Invalid reading hold for beat '${beatId}'.`);
            }
            return {
                id: beatId,
                arrangement: parseCoreConceptArrangement(beat.arrangement),
                explanation: text(beat.explanation),
                readingHoldMs: beat.readingHoldMs,
            };
        });
        return { sceneKind: 'concept', id, title: sceneTitle, beats };
    });
    return { handle, schemaVersion: 2, title, scenes };
}
